<template>
  <div>
    <!-- <p>{{ filters }} {{ fromDate }} {{ toDate }}</p> -->
    <v-data-table
      class="simple-table"
      fixed-header
      :height="recent ? '338px' : '100%'"
      :headers="headers"
      :items="alerts"
      :server-items-length="totalItems"
      item-value="id"
      :options.sync="options"
      :loading="loading"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        itemsPerPageText: $ucfirst($t(`pagination.itemsPerPage`)),
        pageText: $ucfirst($t(`pagination.pageText`)),
      }"
    >
      <template v-slot:no-data>
        {{ "" }}
      </template>

      <template v-if="!recent" v-slot:[`item.subjectId`]="{ item }">
        <span class="alerts-cell--nowrap">{{ item.subjectId }}</span>
      </template>

      <template v-slot:[`item.date`]="{ item }">
        <span class="alerts-cell--nowrap">{{ item.date }}</span>
      </template>

      <template v-slot:[`item.time`]="{ item }">
        <span class="alerts-cell--nowrap">{{ item.time }}</span>
      </template>

      <template v-slot:[`item.emergencyLevel`]="{ item }">
        <span class="alerts-cell--nowrap">{{
          $ucfirst($t(`alerts.${item.emergencyLevel}`))
        }}</span>
      </template>

      <template v-slot:[`item.title`]="{ item }">
        {{ item.title }}
      </template>

      <template v-slot:[`item.message`]="{ item }">
        {{ item.message }}
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <div class="d-flex justify-space-between align-center">
          {{ item.action }}
          <div class="d-flex align-center">
            <v-btn
              v-if="!recent"
              class="ml-4"
              fab
              text
              small
              right
              :to="{
                name: 'PatientOverview',
                params: { patientId: item.patientId },
              }"
            >
              <v-icon dark>mdi-file-search</v-icon>
            </v-btn>
            <handle-alert-button
              :state="item.state"
              :alert="item"
              :is-recent="recent"
              v-on:handle-confirmed="fetchData"
            ></handle-alert-button>
          </div>
        </div>
      </template>
    </v-data-table>
    <div class="alerts__controls-bottom">
      <div class="alerts__page-indicator">
        {{
          $ucfirst(
            $t("pagination.showingpage", {
              params: { current: currentPage, total: totalPages },
            })
          )
        }}
      </div>
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        total-visible="7"
        light
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import HandleAlertButton from "../Parts/HandleAlertButton";
import { getAlerts as getAlertsQuery } from "../../../store/alerts/queries";
import moment from "moment";
import { mapGetters } from "vuex";
import { getAlertsQueryConditions } from "../../../store/alerts/actions";
// import { alertTypes } from "../../../store/alerts/getters";

const alertTypes = new Map([
  [1, "yellow"],
  [2, "orange"],
  [3, "red"],
]);

export default {
  name: "AlertsTable",
  components: {
    HandleAlertButton,
  },
  data() {
    const headers = [
      { text: this.$ucfirst(this.$t("alerts.date")), value: "activateDate" },
      { text: this.$ucfirst(this.$t("alerts.time")), value: "time" },
      {
        text: this.$ucfirst(this.$t("alerts.type")),
        value: "emergencyLevel",
      },
      { text: this.$ucfirst(this.$t("alerts.module")), value: "title" },
      {
        text: this.$ucfirst(this.$t("alerts.message")),
        value: "message",
        sortable: false,
      },
      {
        text: this.$ucfirst(this.$t("alerts.action")),
        value: "action",
        sortable: false,
      },
    ];

    if (!this.recent) {
      headers.unshift({
        text: this.$ucfirst(this.$t("people.patient")),
        value: "subjectId",
      },)
    }

    return {
      headers: headers,
      alerts: [],
      totalItems: 0,
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["activateDate"],
        sortDesc: [true],
      },
    };
  },
  computed: {
    ...mapGetters({
      fromDate: "alerts/fromDate",
    }),
    toDate() {
      let toDate = this.$store.getters["alerts/toDate"];

      if (toDate) {
        return toDate + ' 23:59:59';
      }

      return '';
    },
    totalPages() {
      if (!this.options?.itemsPerPage) {
        return 1;
      }

      return Math.ceil(this.totalItems / this.options.itemsPerPage) || 1;
    },
    currentPage: {
      get() {
        return this.options.page;
      },
      set(page) {
        this.options.page = page;
      },
    },
  },
  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    patientId: {
      default: () => null,
    },
    recent: {
      type: Boolean,
      default: () => false,
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchData(false);
      },
      deep: true,
    },
    filters: {
      handler() {
        this.fetchData(false);
      },
      deep: true,
    },
    patientId: {
      handler() {
        if (this.patientId !== "") {
          this.fetchData();
        }
      },
    },
    fromDate: {
      handler() {
        this.fetchData();
      },
    },
    toDate: {
      handler() {
        this.fetchData();
      },
    },
  },
  methods: {
    emitAlertCount(alertCounts) {
      this.$emit('update:alertCount', alertCounts ? alertCounts : {
        red: 0,
        orange: 0,
        yellow: 0,
        handled: 0,
        active: 0,
      });
    },
    async fetchAlerts(conditions, orders, limit = null, offset = null) {
      return await this.$app.apolloClient.query({
        query: getAlertsQuery,
        variables: {
          limit: limit !== null ? limit : this.options.itemsPerPage,
          offset: offset !== null ? offset : (this.options.page - 1) * this.options.itemsPerPage,
          orders,
          ...conditions,
          functions: this.patientId
              ? [{ function: "patientId", parameters: [this.patientId.toString()] }]
              : [],
        },
      });
    },
    async fetchData(andUpdateCounts = true) {
      try {
        this.loading = true;

        const orders = [];

        if (this.options.sortBy.length) {
          switch (this.options.sortBy[0]) {
            case "time":
              orders.push({
                function: "time",
                direction: this.options.sortDesc[0] ? "desc" : "asc",
              });
              break;
            case "title":
              orders.push({
                function: "title",
                direction: this.options.sortDesc[0] ? "desc" : "asc",
              });
              break;
            default:
              orders.push({
                field: this.options.sortBy[0],
                direction: this.options.sortDesc[0] ? "desc" : "asc",
              });
              break;
          }
        }

        let conditions = {
          gate: "and",
          inputs: [],
        };

        conditions = getAlertsQueryConditions(
            this.recent ? null : this.fromDate,
            this.recent ? null : this.toDate,
          this.filters,
          this.recent
        );

        const { data, errors } = await this.fetchAlerts(conditions, orders);

        if (data && !errors) {
  
          // If current page is greater than total pages, move to last page
          const totalPages = Math.ceil(data.alerts.count / this.options.itemsPerPage) || 1;
          if (this.options.page > totalPages) {
            this.options.page = totalPages;
            // Don't continue, let watcher trigger fetchData again with correct page
            return;
          }

          if (andUpdateCounts) {
            const counts = {
              red: 0,
              orange: 0,
              yellow: 0,
              handled: 0,
              active: 0,
            };

            const fetchCountPromise = [];

            for (let countFields in counts) {
              conditions = getAlertsQueryConditions(
                  this.recent ? null : this.fromDate,
                  this.recent ? null : this.toDate,
                  this.filters,
                  this.recent
              );

              // Filter out any existing filter related to the state or emergency level
              conditions.conditions.inputs = conditions.conditions.inputs.filter(
                  obj => obj.field !== 'state' &&  obj.field !== 'emergencyLevel'
              )

              // Do fetches for alert state (active/handled)
              if (countFields === 'handled' || countFields === 'active') {
                // Add our own state to the count query.
                conditions.conditions.inputs.push({
                  field: "state",
                  parameters: [countFields],
                });
              } else {
                // Do fetches for the alert colors (reminder emergency levels)
                const alertLevel = (countFields === 'red') ? 3 : (countFields === 'orange') ? 2 : 1;
                conditions.conditions.inputs.push({
                  field: "emergencyLevel",
                  parameters: [alertLevel.toString()],
                });
                // Make sure only active/handled alerts are counted.
                conditions.conditions.inputs.push({
                  field: "state",
                  operator: "in",
                  parameters: ["active", "handled"],
                });
              }

              fetchCountPromise.push(this.fetchAlerts(conditions, orders, 1, 0));
            }

            await Promise.all(fetchCountPromise).then(results => {
              let index=0;

              for (let alertColor in counts) {
                counts[alertColor] =  results[index++].data.alerts.count;
              }
              this.emitAlertCount(counts);
            });
          }

          // Only update the row data now that all counts have been fetched, this way row data updates together with
          // count numbers in the interface.
          this.totalItems = data.alerts.count;
          this.alerts = data.alerts.edges.map((edge) => {
            const node = edge.node;

            return {
              id: node.id,
              state: node.state,
              patientId: node.user.patient.patientId.toString(),
              subjectId: node.user.subjectId,
              activateDate: node.activateDate.slice(0, 10),
              time: node.activateDate.slice(11, 16),
              emergencyLevel: alertTypes.get(node.emergencyLevel),
              title: node.title,
              message: node.message,
              action: node.actionMessage
            };
          });
        } else if (errors.length) {
          errors.forEach((error) => {
            this.$store.dispatch("error/submit", error, { root: true });
          });
          this.alerts = [];
        }
      } catch (error) {
        this.$store.dispatch("error/submit", error, { root: true });
        this.alerts = [];
        this.emitAlertCount();
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style lang="scss">
.alerts-cell--nowrap {
  white-space: nowrap;
}
</style>
